import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9224Api, F924, F9241 } from '../../../services/pw9224Api';
import { Mode } from './usePW9224Mode';
import { useAuth } from '../../../contexts/AuthContext';

export function usePW9224Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<F924 | null>(null);
  const [formData, setFormData] = useState<Partial<F924>>({});
  const [details, setDetails] = useState<Partial<F9241>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pw9224Api.get(id);
      setRecord(data);
      setFormData(data);
      setDetails(data.details || []);
      setSelectedRow(0);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof F924, value: F924[keyof F924]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof F924): F924[keyof F924] => {
    return (formData as Record<string, unknown>)[key] as F924[keyof F924];
  }, [formData]);

  const setDetailField = useCallback((idx: number, key: keyof F9241, value: F9241[keyof F9241]) => {
    setDetails(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  const getDetailField = useCallback((idx: number, key: keyof F9241): F9241[keyof F9241] => {
    if (idx < 0 || idx >= details.length) return '' as F9241[keyof F9241];
    return (details[idx] as Record<string, unknown>)[key] as F9241[keyof F9241];
  }, [details]);

  const handleSave = useCallback(async (mode: Mode, onSuccess: (result: F924) => void) => {
    try {
      const req = { master: formData as F924, details: details as F9241[] };
      if (mode === 'add') {
        const result = await pw9224Api.create(req);
        message.success(`新增成功: ${result.f924_01}`);
        onSuccess(result);
      } else {
        const result = await pw9224Api.update(formData.f924_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        message.error(errors.map((er: { message: string }) => er.message).join('; '));
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9224Api.delete(record.f924_01);
      message.success('刪除成功');
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '刪除失敗');
      return false;
    }
  }, [record]);

  const addDetailRow = useCallback(() => {
    setDetails(prev => {
      const seq = prev.length + 1;
      return [...prev, { f9241_02: seq, f9241_91: user?.userId || '' }];
    });
  }, [user]);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, f9241_02: i + 1 }));
    });
    setSelectedRow(0);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData(record);
      setDetails(record.details || []);
      setSelectedRow(0);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    const now = new Date().toISOString();
    setFormData({
      f924_11: user?.userId || '',
      f924_12: now,
      f924_17: 1,
      f924_21: now,
      f924_23: now,
      f924_91: user?.userId || '',
    });
    // Create 10 blank detail rows
    const blankDetails: Partial<F9241>[] = [];
    for (let i = 1; i <= 10; i++) {
      blankDetails.push({
        f9241_02: i,
        f9241_11: now,
        f9241_91: user?.userId || '',
      });
    }
    setDetails(blankDetails);
    setSelectedRow(0);
    setRecord(null);
  }, [user]);

  return {
    record, formData, details, selectedRow, loading,
    setRecord, setFormData, setDetails, setSelectedRow,
    loadRecord, setField, getField, setDetailField, getDetailField,
    handleSave, handleDelete, addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd,
  };
}
